import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { Map, Set } from 'immutable';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';

import { DcIcons } from '../../../../ui/app-dc.icons';
import { ColumnConfigItemEntity } from '../../../domain/column-config-item.entity';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../../../domain/diagnostic-errors.entity';
import { ProfileItemEntity } from '../../../domain/profile-item.entity';
import { SubscriptionProvider } from '../../../providers/subscription.provider';
import { SubscriptionSelector } from '../../../store';
import {
	ProfileConfigComponent,
	ProfileConfigStep,
} from '../../profile-config/profile-config.component';
import {
	IProfileActionConfirmationModalData,
	ProfileActionConfirmPopupComponent,
} from '../profile-action-confirm-popup/profile-action-confirm-popup.component';

export enum ProfileConfigAction {
	Create = 'create',
	Update = 'update',
}

@Component({
	selector: 'app-profile-config-modal',
	templateUrl: './profile-config-modal.component.html',
	styleUrls: ['./profile-config-modal.component.scss'],
	providers: [SubscriptionProvider],
})
export class ProfileConfigModalComponent<
		T extends { profileConfig: ProfileItemEntity; action: ProfileConfigAction }
	>
	extends DcBaseComponent
	implements OnInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly ProfileConfigAction = ProfileConfigAction;

	public vo$: Observable<{
		profileConfig: ProfileItemEntity;
		availableColumns: Map<string, ColumnConfigItemEntity>;
		action: ProfileConfigAction;
		error: SubscriptionDiagnosticError | undefined;
		criticErrorCount: number;
	}>;

	@ViewChild(ProfileConfigComponent)
	private readonly profileConfigCmp: ProfileConfigComponent | null = null;

	private readonly selectedPanelStep$ = new BehaviorSubject<number>(0);

	public constructor(
		private readonly modalService: AppModalService,
		private readonly selector: SubscriptionSelector,
		private readonly subscriptionProvider: SubscriptionProvider,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'profile-config-modal';

		this.subscriptionProvider.configure({
			[ProfileConfigStep.ProfileMetadata]: [
				SubscriptionErrorKey.ProfileMissingFields,
			],
			[ProfileConfigStep.ColumnModel]: [
				SubscriptionErrorKey.NoSelectColumnModel,
			],
			[ProfileConfigStep.FilterModel]: [
				SubscriptionErrorKey.NoSelectFilterModel,
			],
		});

		const profileConfigErrors$ = this.subscriptionProvider.errors$;

		this.vo$ = combineAll({
			profileConfig: this.modalData.profileConfig,
			availableColumns: this.selector
				.getSubscriptionColumns$()
				.pipe(
					map((columns) => columns.filter((col) => col.isSelectedInSource))
				),
			action: this.modalData.action,
			error: combineAll({
				step: this.selectedPanelStep$,
				errors: this.subscriptionProvider.errors$,
			}).pipe(
				map(({ step }) =>
					this.subscriptionProvider.getErrorsForStep(step).valueSeq().first()
				)
			),
			criticErrorCount: profileConfigErrors$.pipe(
				map(() => this.subscriptionProvider.getCriticalErrors().size)
			),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
	}

	public changeStep(step: number): void {
		this.selectedPanelStep$.next(step);
	}

	public createAction(): void {
		if (!this.profileConfigCmp) {
			return;
		}

		const profileConfig = this.profileConfigCmp.getProfileConfig();
		if (!profileConfig) {
			return;
		}

		this.modalService.closeActiveModal(profileConfig);
	}

	public async updateAction(): Promise<void> {
		if (!this.profileConfigCmp || !this.modalData.profileConfig) {
			return;
		}

		const profileConfig = this.profileConfigCmp.getProfileConfig();
		if (!profileConfig) {
			return;
		}

		const { metadata, columnModels, filterModels } = profileConfig;
		const [selectedColumnModel] = columnModels;

		const updatedProfile = metadata
			.setColumnModelId(selectedColumnModel.id)
			.setFilterModelIds(Set(filterModels.map((f) => f.id)));

		if (this.modalData.profileConfig.isEqualTo(updatedProfile)) {
			return this.modalService.closeActiveModal(profileConfig);
		}

		const consumers = await firstValueFrom(
			this.selector.getConsumedProjects$()
		);

		const isConsumed = consumers.some(
			(c) => c.profileUuid === this.modalData.profileConfig.id
		);

		if (!isConsumed) {
			return this.modalService.closeActiveModal(profileConfig);
		}

		const modalConfirm =
			this.modalService.openPopup<IProfileActionConfirmationModalData>(
				ProfileActionConfirmPopupComponent,
				{
					title: $localize`:i18n=@@subscription.profile.action.confirmation.popup.update.header:`,
					subTitle: '',
					id: this.modalData.profileConfig.id,
					element: this.modalData.profileConfig.label,
					action: 'UPDATE_PROFILE',
				},
				{
					disableClose: true,
				}
			);

		const modalConfirmResult = await firstValueFrom(modalConfirm.afterClosed());

		if (!modalConfirmResult) {
			return;
		}

		this.modalService.closeActiveModal(profileConfig);
	}
}
